import { createSlice } from "@reduxjs/toolkit";
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchInvoiceData,
  fetchUploadedInvoices,
  deleteInvoice,
  downloadInvoice
} from "./thunk";

export const initialState = {
  uploadedInvoices: [],
  totalCount: 0,
  currentInvoice: null,
  uploadProgress: 0,
  processingStatus: null,
  invoiceData: null,
  preSignedData: null,
  listLoading: false,
  invoiceLoading: false,
  deleteLoading: false,
  loading: false,
  error: null
};

const InvoiceSlice = createSlice({
  name: 'Invoice',
  initialState,
  reducers: {
    // Custom reducer for upload progress
    uploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetUpload: (state) => {
      state.currentInvoice = null;
      state.uploadProgress = 0;
      state.preSignedData = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Request Pre-signed URL
    builder.addCase(requestPresignedUrl.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(requestPresignedUrl.fulfilled, (state, action) => {
      state.preSignedData = action?.payload;
      state.loading = false;
    });
    builder.addCase(requestPresignedUrl.rejected, (state, action) => {
      state.error = action?.payload || 'Failed to get upload URL';
      state.loading = false;
    });

    // Upload File to S3
    builder.addCase(uploadFileToS3.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadFileToS3.fulfilled, (state, action) => {
      state.currentInvoice = action?.payload;
      state.loading = false;
      state.uploadProgress = 100;
    });
    builder.addCase(uploadFileToS3.rejected, (state, action) => {
      state.error = action?.payload || 'Upload failed';
      state.loading = false;
    });

    // Fetch Invoice Data
    builder.addCase(fetchInvoiceData.pending, (state) => {
      state.invoiceLoading = true;
    });
    builder.addCase(fetchInvoiceData.fulfilled, (state, action) => {
      state.invoiceData = action?.payload;
      state.invoiceLoading = false;
    });
    builder.addCase(fetchInvoiceData.rejected, (state, action) => {
      state.error = action?.payload || 'Invoice data fetch failed';
      state.invoiceLoading = false;
    });

    // Fetch Uploaded Invoices
    builder.addCase(fetchUploadedInvoices.pending, (state) => {
      state.listLoading = true;
      state.error = null; // Clear error when starting a new fetch
    });
    builder.addCase(fetchUploadedInvoices.fulfilled, (state, action) => {
      state.uploadedInvoices = action?.payload?.summaries || [];
      state.totalCount = action?.payload?.total_count || 0;
      state.listLoading = false;
      state.error = null; // Clear error on success
    });
    builder.addCase(fetchUploadedInvoices.rejected, (state, action) => {
      state.error = action?.payload || 'Failed to load invoices';
      state.listLoading = false;
    });

    // Delete Invoice
    builder.addCase(deleteInvoice.pending, (state) => {
      state.deleteLoading = true;
    });
    builder.addCase(deleteInvoice.fulfilled, (state, action) => {
      state.uploadedInvoices = state.uploadedInvoices.filter(
        invoice => invoice.invoice_id !== action?.payload?.invoiceId
      );
      state.deleteLoading = false;
    });
    builder.addCase(deleteInvoice.rejected, (state, action) => {
      state.error = action?.payload || 'Delete failed';
      state.deleteLoading = false;
    });

    // Download Invoice
    builder.addCase(downloadInvoice.rejected, (state, action) => {
      state.error = action?.payload || 'Download failed';
    });
  }
});

export const { uploadProgress, resetUpload, clearError } = InvoiceSlice.actions;
export default InvoiceSlice.reducer;
